// Legal Widget — niche content editor (`/legal/:slug`).
//
// Structured editor for ONE niche's full content set, ported from the
// legal-widget dashboard's NicheEditor onto admin-web's theme primitives:
//   - Partners: managed add-order list (add / remove / reorder). We NEVER
//     dedupe or sort at storage — that happens at render. The raw add-order
//     array is kept exactly as the operator built it.
//   - Legal sections: per page kind, a heading input + a <textarea> rich-text
//     body. A token palette inserts LITERAL `{siteName}` etc. at the cursor.
//   - Consent: intro + items (label, bodyHtml, role) + disclaimer + accordion.
//   - Footer: links (label/href) + disclaimer + copyright.
//
// CRITICAL invariants (enforced by the backend on the write path; this UI
// respects them):
//   - SANITIZE ON WRITE: Save/Publish send RAW editor HTML; the server runs
//     sanitizeRichText() before persisting. The SPA does not pre-sanitize.
//   - TOKENS STORED LITERAL: the palette inserts `{siteName}` verbatim.
//   - PUBLISH BUMPS VERSION: "Save" (PUT) persists without a version bump;
//     "Publish" (POST .../publish) increments `version`.
//   - NICHE ISOLATION: every call is keyed by this `:slug` only.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import EmbedSnippets from "../../components/EmbedSnippets";
import {
  CONSENT_ROLES,
  legalApi,
  RENDER_TOKENS,
  type ConsentItem,
  type FooterLink,
  type LegalPage,
  type Niche,
} from "../../lib/legalApi";
import {
  buttonStyle,
  Card,
  EmptyState,
  FieldLabel,
  inputStyle,
  PageHeader,
  Pill,
  Skeleton,
  tokens,
} from "../../lib/theme";

/** A <textarea> rich-text surface with a token-insertion palette. Inserts the
 *  literal `{token}` at the caret. */
function RichText({
  id,
  value,
  onChange,
  rows = 4,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  rows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const insertToken = useCallback(
    (token: string) => {
      const el = ref.current;
      const literal = `{${token}}`;
      if (!el) {
        onChange(value + literal);
        return;
      }
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? value.length;
      const next = value.slice(0, start) + literal + value.slice(end);
      onChange(next);
      // Restore caret just after the inserted token on the next tick.
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + literal.length;
        el.setSelectionRange(pos, pos);
      });
    },
    [onChange, value],
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: tokens.space.xs,
          marginBottom: tokens.space.xs,
        }}
      >
        <span
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.xs,
            marginRight: tokens.space.xs,
          }}
        >
          Insert token:
        </span>
        {RENDER_TOKENS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => insertToken(t)}
            style={{
              border: "1px solid rgba(0,212,170,0.30)",
              background: "rgba(0,212,170,0.08)",
              color: tokens.color.tealLight,
              borderRadius: tokens.radius.sm,
              padding: "2px 6px",
              fontSize: tokens.font.size.xs,
              fontFamily: tokens.font.mono,
              cursor: "pointer",
            }}
          >
            {`{${t}}`}
          </button>
        ))}
      </div>
      <textarea
        id={id}
        ref={ref}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...inputStyle,
          fontFamily: tokens.font.mono,
          resize: "vertical",
        }}
      />
    </div>
  );
}

export default function LegalNicheEditor() {
  const params = useParams();
  const slug = params["slug"] ?? "";
  const [niche, setNiche] = useState<Niche | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newPartner, setNewPartner] = useState("");

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setNiche(null);
    legalApi
      .getNiche(slug)
      .then((n) => {
        if (!cancelled) setNiche(n);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // --- mutators (all operate on a shallow-cloned draft; never sort/dedupe) ---
  function update(mutate: (draft: Niche) => void) {
    setNiche((cur) => {
      if (!cur) return cur;
      const draft = structuredClone(cur);
      mutate(draft);
      return draft;
    });
    setStatus(null);
  }

  function addPartner() {
    const name = newPartner.trim();
    if (!name) return;
    update((d) => {
      // Preserve add-order; do NOT dedupe/sort here (render does that).
      d.partners.partners.push(name);
    });
    setNewPartner("");
  }
  function removePartner(idx: number) {
    update((d) => {
      d.partners.partners.splice(idx, 1);
    });
  }
  function movePartner(idx: number, dir: -1 | 1) {
    update((d) => {
      const arr = d.partners.partners;
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return;
      const a = arr[idx];
      const b = arr[j];
      if (a === undefined || b === undefined) return;
      arr[idx] = b;
      arr[j] = a;
    });
  }

  async function persist(kind: "save" | "publish") {
    if (!niche) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const saved =
        kind === "publish"
          ? await legalApi.publishNiche(slug, niche)
          : await legalApi.saveNiche(slug, niche);
      setNiche(saved);
      setStatus(
        kind === "publish"
          ? `Published — version is now v${saved.version}.`
          : "Saved (rich text sanitized server-side; version unchanged).",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  if (error && !niche) {
    return (
      <div>
        <PageHeader
          title={`Editor · ${slug}`}
          actions={<Link to="/legal">← All niches</Link>}
        />
        <Card>
          <EmptyState title="Failed to load niche" hint={error} />
        </Card>
      </div>
    );
  }

  if (!niche) {
    return (
      <div>
        <PageHeader title={<Skeleton width={260} height={26} />} />
        <Skeleton height={160} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={niche.displayName}
        subtitle={
          <span style={{ fontFamily: tokens.font.mono }}>{niche.slug}</span>
        }
        actions={
          <>
            <Link
              to={`/legal/${encodeURIComponent(slug)}/observability`}
              style={{ alignSelf: "center" }}
            >
              Observability
            </Link>
            <Pill tone="neutral">v{niche.version}</Pill>
            <button
              type="button"
              onClick={() => void persist("save")}
              disabled={busy}
              style={buttonStyle("ghost", busy)}
            >
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => void persist("publish")}
              disabled={busy}
              style={buttonStyle("primary", busy)}
            >
              Publish
            </button>
          </>
        }
      />

      {error && (
        <div
          role="alert"
          style={{
            marginBottom: tokens.space.lg,
            padding: tokens.space.md,
            background: tokens.color.dangerBg,
            border: `1px solid ${tokens.color.dangerBorder}`,
            color: tokens.color.danger,
            borderRadius: tokens.radius.sm,
            fontSize: tokens.font.size.sm,
          }}
        >
          {error}
        </div>
      )}
      {status && (
        <div
          role="status"
          style={{
            marginBottom: tokens.space.lg,
            padding: tokens.space.md,
            background: "rgba(0,212,170,0.08)",
            border: "1px solid rgba(0,212,170,0.30)",
            color: tokens.color.tealLight,
            borderRadius: tokens.radius.sm,
            fontSize: tokens.font.size.sm,
          }}
        >
          {status}
        </div>
      )}

      {/* ───────────── Embed snippets (what devs paste on landers) ───────────── */}
      <div style={{ marginBottom: tokens.space.xl }}>
        <EmbedSnippets slug={niche.slug} />
      </div>

      {/* ───────────── Partners ───────────── */}
      <Card title="Partners" style={{ marginBottom: tokens.space.xl }}>
        <p
          style={{
            color: tokens.color.fg1,
            margin: `0 0 ${tokens.space.md}px`,
            fontSize: tokens.font.size.sm,
          }}
        >
          Add-order list — stored exactly as ordered here; deduped + sorted only
          at render.
        </p>
        <div
          style={{
            display: "flex",
            gap: tokens.space.sm,
            marginBottom: tokens.space.md,
          }}
        >
          <input
            aria-label="New partner name"
            value={newPartner}
            onChange={(e) => setNewPartner(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addPartner();
              }
            }}
            placeholder="Partner name"
            style={inputStyle}
          />
          <button type="button" onClick={addPartner} style={buttonStyle("subtle")}>
            Add
          </button>
        </div>
        {niche.partners.partners.length === 0 ? (
          <p style={{ color: tokens.color.fg2, margin: 0 }}>No partners yet.</p>
        ) : (
          <ol style={{ margin: 0, paddingLeft: tokens.space.xl }}>
            {niche.partners.partners.map((p, idx) => (
              <li key={`${p}-${idx}`} style={{ marginBottom: tokens.space.xs }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: tokens.space.sm,
                  }}
                >
                  <span style={{ color: tokens.color.fg0 }}>{p}</span>
                  <button
                    type="button"
                    aria-label={`Move ${p} up`}
                    onClick={() => movePartner(idx, -1)}
                    style={miniBtn}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${p} down`}
                    onClick={() => movePartner(idx, 1)}
                    style={miniBtn}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${p}`}
                    onClick={() => removePartner(idx)}
                    style={{
                      ...miniBtn,
                      color: tokens.color.danger,
                      borderColor: tokens.color.dangerBorder,
                    }}
                  >
                    ✕
                  </button>
                </span>
              </li>
            ))}
          </ol>
        )}
      </Card>

      {/* ───────────── Legal pages & sections ───────────── */}
      <Card
        title="Legal pages & sections"
        style={{ marginBottom: tokens.space.xl }}
      >
        <p
          style={{
            color: tokens.color.fg1,
            margin: `0 0 ${tokens.space.md}px`,
            fontSize: tokens.font.size.sm,
          }}
        >
          Rich text is sanitized server-side on save. Insert tokens as literal
          text.
        </p>
        {niche.pages.map((page: LegalPage, pIdx) => (
          <details
            key={page.kind}
            open={pIdx === 0}
            style={{
              marginBottom: tokens.space.md,
              border: `1px solid ${tokens.color.border}`,
              borderRadius: tokens.radius.md,
              padding: tokens.space.md,
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontWeight: 600,
                color: tokens.color.fg0,
              }}
            >
              {page.title}{" "}
              <span style={{ color: tokens.color.fg2 }}>({page.kind})</span>
            </summary>
            <div style={{ paddingTop: tokens.space.md }}>
              <FieldLabel htmlFor={`title-${page.kind}`}>Page title</FieldLabel>
              <input
                id={`title-${page.kind}`}
                value={page.title}
                onChange={(e) =>
                  update((d) => {
                    const pg = d.pages[pIdx];
                    if (pg) pg.title = e.target.value;
                  })
                }
                style={{ ...inputStyle, marginBottom: tokens.space.md }}
              />
              {page.sections.map((sec, sIdx) => (
                <div
                  key={sec.id}
                  style={{
                    border: `1px solid ${tokens.color.border}`,
                    borderRadius: tokens.radius.sm,
                    padding: tokens.space.md,
                    marginBottom: tokens.space.md,
                    background: tokens.color.bg1,
                  }}
                >
                  <FieldLabel htmlFor={`heading-${page.kind}-${sec.id}`}>
                    Section heading
                  </FieldLabel>
                  <input
                    id={`heading-${page.kind}-${sec.id}`}
                    value={sec.heading}
                    onChange={(e) =>
                      update((d) => {
                        const s = d.pages[pIdx]?.sections[sIdx];
                        if (s) s.heading = e.target.value;
                      })
                    }
                    style={{ ...inputStyle, marginBottom: tokens.space.md }}
                  />
                  <FieldLabel htmlFor={`body-${page.kind}-${sec.id}`}>
                    Body (rich text)
                  </FieldLabel>
                  <RichText
                    id={`body-${page.kind}-${sec.id}`}
                    value={sec.bodyHtml}
                    rows={6}
                    onChange={(next) =>
                      update((d) => {
                        const s = d.pages[pIdx]?.sections[sIdx];
                        if (s) s.bodyHtml = next;
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </details>
        ))}
      </Card>

      {/* ───────────── Consent block ───────────── */}
      <Card title="Consent block" style={{ marginBottom: tokens.space.xl }}>
        <FieldLabel htmlFor="consent-intro">Intro</FieldLabel>
        <div style={{ marginBottom: tokens.space.md }}>
          <RichText
            id="consent-intro"
            value={niche.consent.introHtml}
            onChange={(next) => update((d) => void (d.consent.introHtml = next))}
          />
        </div>

        {niche.consent.items.map((item: ConsentItem, iIdx) => (
          <div
            key={item.id}
            style={{
              border: `1px solid ${tokens.color.border}`,
              borderRadius: tokens.radius.sm,
              padding: tokens.space.md,
              marginBottom: tokens.space.md,
              background: tokens.color.bg1,
            }}
          >
            <FieldLabel htmlFor={`consent-label-${item.id}`}>
              Label (bold lead-in)
            </FieldLabel>
            <input
              id={`consent-label-${item.id}`}
              value={item.label}
              onChange={(e) =>
                update((d) => {
                  const it = d.consent.items[iIdx];
                  if (it) it.label = e.target.value;
                })
              }
              style={{ ...inputStyle, marginBottom: tokens.space.md }}
            />
            <FieldLabel htmlFor={`consent-body-${item.id}`}>
              Body (rich text)
            </FieldLabel>
            <div style={{ marginBottom: tokens.space.md }}>
              <RichText
                id={`consent-body-${item.id}`}
                value={item.bodyHtml}
                onChange={(next) =>
                  update((d) => {
                    const it = d.consent.items[iIdx];
                    if (it) it.bodyHtml = next;
                  })
                }
              />
            </div>
            <FieldLabel htmlFor={`consent-role-${item.id}`}>
              TrustedForm role
            </FieldLabel>
            <select
              id={`consent-role-${item.id}`}
              value={item.role}
              onChange={(e) =>
                update((d) => {
                  const it = d.consent.items[iIdx];
                  if (it) it.role = e.target.value as ConsentItem["role"];
                })
              }
              style={inputStyle}
            >
              {CONSENT_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        ))}

        <FieldLabel htmlFor="consent-disclaimer">Disclaimer</FieldLabel>
        <div style={{ marginBottom: tokens.space.md }}>
          <RichText
            id="consent-disclaimer"
            value={niche.consent.disclaimerHtml}
            onChange={(next) =>
              update((d) => void (d.consent.disclaimerHtml = next))
            }
          />
        </div>
        <FieldLabel htmlFor="consent-accordion">
          Partners accordion label
        </FieldLabel>
        <input
          id="consent-accordion"
          value={niche.consent.partnersAccordionLabel}
          onChange={(e) =>
            update((d) => void (d.consent.partnersAccordionLabel = e.target.value))
          }
          style={inputStyle}
        />
      </Card>

      {/* ───────────── Footer ───────────── */}
      <Card title="Footer">
        <h3
          style={{
            margin: `0 0 ${tokens.space.sm}px`,
            color: tokens.color.fg1,
            fontSize: tokens.font.size.sm,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          Links
        </h3>
        {niche.footer.links.length === 0 ? (
          <p style={{ color: tokens.color.fg2, marginTop: 0 }}>No links yet.</p>
        ) : (
          <div
            style={{
              display: "grid",
              gap: tokens.space.sm,
              marginBottom: tokens.space.md,
            }}
          >
            {niche.footer.links.map((lnk: FooterLink, lIdx) => (
              <div
                key={lIdx}
                style={{
                  display: "flex",
                  gap: tokens.space.sm,
                  alignItems: "center",
                }}
              >
                <input
                  aria-label={`Footer link ${lIdx + 1} label`}
                  value={lnk.label}
                  placeholder="Label"
                  onChange={(e) =>
                    update((d) => {
                      const l = d.footer.links[lIdx];
                      if (l) l.label = e.target.value;
                    })
                  }
                  style={inputStyle}
                />
                <input
                  aria-label={`Footer link ${lIdx + 1} href`}
                  value={lnk.href}
                  placeholder="Href"
                  onChange={(e) =>
                    update((d) => {
                      const l = d.footer.links[lIdx];
                      if (l) l.href = e.target.value;
                    })
                  }
                  style={inputStyle}
                />
                <button
                  type="button"
                  aria-label={`Remove footer link ${lIdx + 1}`}
                  onClick={() =>
                    update((d) => {
                      d.footer.links.splice(lIdx, 1);
                    })
                  }
                  style={{
                    ...miniBtn,
                    color: tokens.color.danger,
                    borderColor: tokens.color.dangerBorder,
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() =>
            update((d) => {
              d.footer.links.push({ label: "", href: "" });
            })
          }
          style={buttonStyle("subtle")}
        >
          Add link
        </button>

        <div style={{ marginTop: tokens.space.lg }}>
          <FieldLabel htmlFor="footer-disclaimer">Disclaimer</FieldLabel>
          <div style={{ marginBottom: tokens.space.md }}>
            <RichText
              id="footer-disclaimer"
              value={niche.footer.disclaimerHtml}
              onChange={(next) =>
                update((d) => void (d.footer.disclaimerHtml = next))
              }
            />
          </div>
          <FieldLabel htmlFor="footer-copyright">Copyright</FieldLabel>
          <RichText
            id="footer-copyright"
            value={niche.footer.copyrightHtml}
            rows={2}
            onChange={(next) =>
              update((d) => void (d.footer.copyrightHtml = next))
            }
          />
        </div>
      </Card>
    </div>
  );
}

const miniBtn: React.CSSProperties = {
  border: `1px solid ${tokens.color.borderMid}`,
  background: "rgba(255,255,255,0.04)",
  color: tokens.color.fg1,
  borderRadius: tokens.radius.sm,
  padding: "0 8px",
  fontSize: tokens.font.size.md,
  lineHeight: "24px",
  cursor: "pointer",
};
